<script lang="ts">
  import { AVATARS, DEFAULT_AVATAR, MAX_NAME_LENGTH, type Avatar } from '../../shared/config';
  import Button from '../ui/Button.svelte';
  import Dialog from '../ui/Dialog.svelte';
  import Select from '../ui/Select.svelte';
  import { generateName } from './name-generator';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import { getAppearance } from '../themes/appearance.svelte';
  import { DEFAULT_THEME, THEMES } from '../themes/registry';

  interface Props {
    open?: boolean;
    save: (identity: { name: string; avatar: Avatar }) => void;
  }
  let { open = false, save }: Props = $props();
  const appearance = getAppearance();

  let welcomeName = $state(generateName());
  let welcomeAvatar = $state<Avatar>(DEFAULT_AVATAR);
  let generatedName = true;
  let touchMoved = false;

  function rollName(): void {
    welcomeName = generateName(welcomeName);
    generatedName = true;
  }

  function editName(event: Event): void {
    if (!generatedName) return;
    generatedName = false;
    welcomeName = '';
    // Clear synchronously so typing/pasting replaces the suggestion.
    (event.currentTarget as HTMLInputElement).value = '';
  }

  function focusName(event: TouchEvent): void {
    if (touchMoved || event.touches.length || event.changedTouches.length !== 1) return;
    const input = event.currentTarget as HTMLInputElement;
    if (document.activeElement !== input) {
      // Safari's default input focus pans its native scroll view even when the body is fixed.
      event.preventDefault();
      input.focus({ preventScroll: true });
    }
  }

  function preventFocusScroll(event: FocusEvent): void {
    const next = event.relatedTarget;
    if (next instanceof HTMLInputElement && next.id === 'welcome-name') {
      // Run before Safari's default focus for keyboard/assistive-technology navigation, too.
      next.focus({ preventScroll: true });
    }
  }

  function submit(event: SubmitEvent): void {
    event.preventDefault();
    const value = welcomeName.trim().slice(0, MAX_NAME_LENGTH);
    if (!value) return;
    save({ name: value, avatar: welcomeAvatar });
  }
</script>

<Dialog {open} required welcome labelledby="welcome-title" onBlurCapture={preventFocusScroll}>
  <form onsubmit={submit}>
    <h2 id="welcome-title">Welcome to Spellie</h2>
    <label for="welcome-name">Name</label>
    <div class="welcome-name-row">
      <input
        id="welcome-name"
        bind:value={welcomeName}
        maxlength={MAX_NAME_LENGTH}
        autocomplete="nickname"
        onfocus={editName}
        onpointerdown={editName}
        ontouchstart={() => (touchMoved = false)}
        ontouchmove={() => (touchMoved = true)}
        ontouchend={focusName}
        onbeforeinput={editName}
        oninput={() => (generatedName = false)}
        required
      />
      <Button
        class="name-dice"
        onclick={rollName}
        aria-label="Generate a new name"
        title="Generate a new name"
      >
        <ThemeIcon name="dice" />
      </Button>
    </div>
    <label for="welcome-avatar">Avatar</label>
    <Select
      id="welcome-avatar"
      value={welcomeAvatar}
      onchange={(value) => (welcomeAvatar = value)}
      options={AVATARS.map((value) => ({ value, label: value }))}
    />
    <label for="welcome-theme">Theme</label>
    <Select
      id="welcome-theme"
      aria-label="Theme"
      value={appearance.theme.id}
      onchange={appearance.selectTheme}
      options={THEMES.map((theme) => ({
        value: theme.id,
        label: theme.name + (theme.id === DEFAULT_THEME.id ? ' (default)' : ''),
      }))}
    />
    <!-- svelte-ignore a11y_autofocus (Native modal initial focus avoids opening the mobile keyboard.) -->
    <Button variant="primary" type="submit" autofocus disabled={!welcomeName.trim()}>Continue</Button>
  </form>
</Dialog>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  h2 {
    margin: 0 0 12px;
  }
  .welcome-name-row {
    display: flex;
    align-items: stretch;
    gap: 8px;
    height: 52px;
  }
  .welcome-name-row input {
    flex: 1;
    min-width: 0;
  }
  :global(.btn.name-dice) {
    flex: 0 0 52px;
    padding: 0;
    border: var(--theme-input-border);
    border-radius: var(--theme-input-radius);
    font-size: 26px;
  }
</style>
